<script setup lang="ts">
import { computed } from 'vue'

import type { RunPhase, WizardStatus } from '../../models/impact.types'
import { useImpactStore } from '../../stores/impactStore'

import { useI18n } from '@/shared/i18n'
import { Button, Icon, Spinner } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    executionId?: string | null
    analysisName?: string | null
    status?: WizardStatus
    phase?: RunPhase | null
  }>(),
  { executionId: null, analysisName: null, status: 'idle', phase: null },
)

defineEmits<{ background: []; viewResults: [] }>()

const store = useImpactStore()
const { t } = useI18n()
const ownsActive = computed(
  () => props.executionId !== null && store.active.executionId === props.executionId,
)
const status = computed(() => (ownsActive.value ? store.active.status : props.status))
const phase = computed(() => (ownsActive.value ? store.active.phase : props.phase))
const completed = computed(() => status.value === 'completed' || phase.value === 'completed')
const phaseKey = computed(() => {
  if (status.value === 'saving_settings') return 'impactAnalysis.running.phase.savingSettings'
  if (phase.value === 'running_profile') return 'impactAnalysis.running.phase.runningProfile'
  if (phase.value === 'charging') return 'impactAnalysis.running.phase.charging'
  if (phase.value === 'persisting') return 'impactAnalysis.running.phase.persisting'
  if (status.value === 'running') return 'impactAnalysis.running.phase.runningProfile'
  return 'impactAnalysis.running.phase.queued'
})
</script>

<template>
  <section
    class="running-overlay flex min-h-[21rem] flex-col items-center justify-center py-xl px-lg text-center"
    :aria-label="t('impactAnalysis.running.label')"
  >
    <template v-if="!completed">
      <span
        class="running-overlay__spinner grid w-[8rem] h-[8rem] mb-xl place-items-center rounded-xl text-info bg-info-soft"
        ><Spinner size="xl"
      /></span>
      <h3>{{ t(phaseKey) }}</h3>
      <p>{{ t('impactAnalysis.running.historyHint') }}</p>
      <div class="running-overlay__actions flex flex-col items-center gap-xs">
        <Button variant="primary" size="lg" @click="$emit('background')">
          {{ t('impactAnalysis.running.background') }}
        </Button>
        <small>{{ t('impactAnalysis.running.notifyHint') }}</small>
      </div>
    </template>

    <template v-else>
      <span
        class="running-overlay__complete grid w-[8rem] h-[8rem] mb-xl place-items-center rounded-xl text-info bg-info-soft w-[5rem] h-[5rem] text-success bg-success-soft"
        ><Icon name="check" :size="34"
      /></span>
      <h3>{{ t('impactAnalysis.running.completeTitle') }}</h3>
      <p>{{ analysisName || t('impactAnalysis.page.title') }}</p>
      <Button variant="primary" size="lg" @click="$emit('viewResults')">
        {{ t('impactAnalysis.running.viewResults') }}
        <Icon name="external-link" :size="14" />
      </Button>
    </template>
  </section>
</template>

<style scoped>
.running-overlay .running-overlay__spinner :deep(.ui-spinner__ring) {
  width: 5.25rem;
  height: 5.25rem;
  border-style: solid;
  border-width: 0.5rem;
}
.running-overlay h3 {
  margin-top: 0;
  margin-inline: 0;
  margin-bottom: var(--ui-space-xs);
  color: var(--ui-color-primary);
  font-size: 1.25rem;
  font-weight: 700;
}
.running-overlay p {
  margin-top: 0;
  margin-inline: 0;
  margin-bottom: var(--ui-space-xl);
  color: var(--ui-color-muted);
  font-size: 0.875rem;
}
.running-overlay small {
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
  font-weight: 600;
}
</style>
